/**
 * Palette de GRAPHIQUES de la suite SEmply.
 *
 * ── Pourquoi elle ne se décline pas par palette de marque ────────────────
 *
 * Les sept autres palettes habillent l'interface ; celle-ci encode de
 * l'information. Une couleur de série n'a pas à être « dans le ton » : elle
 * doit rester distinguable de ses voisines, y compris sous protanopie,
 * deutéranopie et tritanopie. Dériver sept teintes de chaque palette de marque
 * reviendrait à refaire neuf fois ce calcul — et à le rater neuf fois.
 *
 * L'échelle ci-dessous est donc la MÊME sous les neuf palettes. Seul le mode
 * clair/sombre la fait basculer, parce que là le fond change vraiment.
 *
 * ── Ce qui a été vérifié ─────────────────────────────────────────────────
 *
 * Échelle catégorielle validée (ordre des teintes FIXE — une série garde sa
 * couleur quel que soit le filtre, jamais de cycle). Sur les fonds réels de la
 * charte, `--bg-color` = #FFFFFF pour les sept palettes claires et #14171C
 * pour les deux sombres :
 *
 *   clair  — séparation CVD ΔE 9,1 au pire (jaune ↔ aqua, protanopie),
 *            vision normale ΔE 19,6 au pire, bandes de clarté et de chroma OK.
 *   sombre — séparation CVD ΔE 8,4, vision normale ΔE 19,3, contraste ≥ 3:1
 *            pour les sept séries.
 *
 * **Règle de relief, en clair uniquement** : `aqua` (2,82:1), `jaune` (2,17:1)
 * et `magenta` (2,69:1) passent sous 3:1 sur fond blanc. Ce n'est pas un
 * défaut à corriger en fonçant les teintes — cela casserait la séparation CVD
 * — mais une obligation pour le composant : ces séries doivent porter un
 * libellé direct visible, ou l'écran doit offrir la vue tableau. Un graphique
 * qui ne distingue ses séries QUE par la couleur est fautif de toute façon.
 *
 * ── Comment on la modifie ────────────────────────────────────────────────
 *
 * Jamais à l'œil. L'outil qui a produit ces chiffres est le validateur de la
 * compétence « dataviz » ; toute retouche se rejoue avec lui, dans les deux
 * modes, avec les fonds ci-dessus. `src/charts.test.js` garde les seuils qui
 * se calculent sans lui (contraste, longueur, forme des valeurs).
 */

/** Nombre de séries. Au-delà, on replie en « Autres » ou on facette. */
export const CHART_SERIES_COUNT = 7;

/** Teintes, dans l'ordre. Le nom sert à la lecture, pas au code. */
export const CHART_SERIES_HUES = [
  'bleu',
  'orange',
  'aqua',
  'jaune',
  'magenta',
  'vert',
  'violet',
];

const CLAIR = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7'];
const SOMBRE = ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#008300', '#9085e9'];

/** `['#2a78d6', …]` → `{ '--chart-series-1': '#2a78d6', … }` */
function enJetons(echelle) {
  return Object.fromEntries(echelle.map((valeur, index) => [`--chart-series-${index + 1}`, valeur]));
}

export const CHART_SERIES_LIGHT = enJetons(CLAIR);
export const CHART_SERIES_DARK = enJetons(SOMBRE);

/** Les jetons à poser sur une palette, selon qu'elle est sombre ou non. */
export function chartVarsFor(palette) {
  return palette?.isDark ? CHART_SERIES_DARK : CHART_SERIES_LIGHT;
}
